import { fixedUi } from '@/i18n/fixedUi';
import React from 'react';
import '@/styles/shein-platform/shein-sync-progress.css';

/**
 * 从同步任务视图提取「已处理 / 总数」与单位文案。
 * 兼容 SKC / SPU / 通用 processed-total 字段。
 */
export function extractSyncProgressCounts(raw = {}) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const imported = Number(source.imported) || 0;

  const skcProcessed = Number(source.skcProcessed);
  const skcTotal = Number(source.skcTotal);
  if ((Number.isFinite(skcProcessed) && skcProcessed > 0) || (Number.isFinite(skcTotal) && skcTotal > 0)) {
    return {
      processed: Math.max(0, skcProcessed || 0),
      total: Math.max(0, skcTotal || 0),
      unit: '个 SKC',
      imported,
    };
  }

  const spuProcessed = Number(source.spuProcessed);
  const spuTotal = Number(source.spuTotal);
  if ((Number.isFinite(spuProcessed) && spuProcessed > 0) || (Number.isFinite(spuTotal) && spuTotal > 0)) {
    return {
      processed: Math.max(0, spuProcessed || 0),
      total: Math.max(0, spuTotal || 0),
      unit: '个 SPU',
      imported,
    };
  }

  const processed = Number(source.processed);
  const total = Number(source.total);
  if ((Number.isFinite(processed) && processed > 0) || (Number.isFinite(total) && total > 0)) {
    return {
      processed: Math.max(0, processed || 0),
      total: Math.max(0, total || 0),
      unit: '条',
      imported,
    };
  }

  return {
    processed: Number(source.processed) || 0,
    total: Number(source.total) || 0,
    unit: '',
    imported,
  };
}

function buildPrimaryMessage(progress, counts) {
  const status = progress?.status || '';
  if (status === 'queued') {
    const ahead = Number(progress?.queueAhead) || 0;
    const total = Number(progress?.queueTotal) || 0;
    const position = Number(progress?.queuePosition) || 0;
    const running = Number(progress?.runningCount) || 0;
    const max = Number(progress?.maxConcurrentShops) || 0;
    const extra = [
      total > 0 ? `${fixedUi('共')} ${total} ${fixedUi('店在排队')}` : '',
      max > 0 ? `${fixedUi('并行')} ${running}/${max}` : '',
    ].filter(Boolean).join(' · ');
    const tail = extra ? ` · ${extra}` : '';
    if (ahead > 0) {
      const place = position > 0 ? `（${fixedUi('您排第')} ${position}）` : '';
      return `${fixedUi('排队中')}：${fixedUi('前方还有')} ${ahead} ${fixedUi('个店铺')}${place}${tail}`;
    }
    if (total > 0 || max > 0) {
      return `${fixedUi('排队中，即将开始')}${tail}`;
    }
  }

  const rawMsg = String(progress?.message || '').trim();
  if (rawMsg) return rawMsg;

  const label = fixedUi(String(progress?.label || '同步').trim() || '同步');
  const { processed, total, unit } = counts;
  const unitText = unit ? ` ${fixedUi(unit)}` : '';
  if (total > 0) {
    return `${fixedUi('已同步')} ${processed}/${total}${unitText}…`;
  }
  if (processed > 0) {
    return `${fixedUi('已同步')} ${processed}${unitText}…`;
  }
  return `${label}…`;
}

/**
 * SHEIN 同步进度条（统一样式）：
 * 上行：进度文案 + 百分比 + 终止；下行：可动效进度轨道。
 */
export default function SheinSyncProgressBar({
  progress,
  onCancel,
  cancelling = false,
  importedLabel = fixedUi('已入库'),
}) {
  if (!progress?.visible) return null;

  const status = progress.status || 'running';
  const percent = Math.round(Math.max(0, Math.min(100, Number(progress.percent) || 0)));
  const counts = extractSyncProgressCounts({
    imported: progress.imported,
    processed: progress.processed,
    total: progress.total,
    skcProcessed: progress.skcProcessed,
    skcTotal: progress.skcTotal,
    spuProcessed: progress.spuProcessed,
    spuTotal: progress.spuTotal,
    ...(progress.raw && typeof progress.raw === 'object' ? progress.raw : {}),
  });
  const imported = counts.imported > 0
    ? counts.imported
    : (Number(progress.imported) || 0);
  const primary = buildPrimaryMessage(progress, counts);
  const active = status === 'running' || status === 'queued';
  const canCancel = typeof onCancel === 'function' && active;
  const queueAhead = Number(progress.queueAhead) || 0;
  const queueTotal = Number(progress.queueTotal) || 0;
  const showQueueChip = status === 'queued' && (queueAhead > 0 || queueTotal > 0);

  return (
    <div
      className={`spo-sync-progress spo-sync-progress--${status}`}
      role="status"
      aria-live="polite"
    >
      <div className="spo-sync-progress__meta">
        <span className="spo-sync-progress__msg" title={primary}>
          <span className="spo-sync-progress__primary">{primary}</span>
          {showQueueChip ? (
            <span className="spo-sync-progress__queue">
              {' · '}
              {fixedUi('前方')} {queueAhead}
              {queueTotal > 0 ? ` / ${fixedUi('排队')} ${queueTotal}` : ''}
            </span>
          ) : null}
          {imported > 0 ? (
            <span className="spo-sync-progress__imported">
              {' · '}
              {importedLabel}
              {' '}
              {imported}
              {' '}
              {fixedUi('条')}
            </span>
          ) : null}
        </span>
        <div className="spo-sync-progress__aside">
          {status === 'queued' ? (
            <span className="spo-sync-progress__pct spo-sync-progress__pct--queue">{fixedUi("排队")}</span>
          ) : (
            <span className="spo-sync-progress__pct">{percent}%</span>
          )}
          {canCancel ? (
            <button
              type="button"
              className="spo-sync-progress__cancel"
              onClick={onCancel}
              disabled={cancelling}
            >
              {cancelling ? fixedUi('终止中…') : fixedUi('终止')}
            </button>
          ) : null}
        </div>
      </div>

      <div className="spo-sync-progress__track" aria-hidden="true">
        <i
          className={
            active
              ? `spo-sync-progress__fill spo-sync-progress__fill--active${status === 'queued' ? ' spo-sync-progress__fill--queued' : ''}`
              : 'spo-sync-progress__fill'
          }
          style={{ width: `${Math.max(percent, status === 'queued' ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
}
